import React, { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { useLang } from "@/contexts/LangContext";

const ease = [0.22, 1, 0.36, 1];

const ALL_CASES = [
  {
    id: 1,
    cat: { es: "IA", en: "AI" },
    catKey: "AI",
    title: { es: "Asistente comercial IA · Sector Salud", en: "AI Sales Assistant · Healthcare" },
    metric: { es: "+312% leads calificados", en: "+312% qualified leads" },
    img: "https://reinventemercadeo.com/wp-content/uploads/2026/03/ai-reinvente-scaled.png",
    accent: "blue",
  },
  {
    id: 2,
    cat: { es: "Neuromarketing", en: "Neuromarketing" },
    catKey: "Neuromarketing",
    title: { es: "Rediseño de copy con base científica", en: "Neuro-based copy redesign" },
    metric: { es: "+58% conversión orgánica", en: "+58% organic conversion" },
    img: "https://reinventemercadeo.com/wp-content/uploads/2026/03/neuromarketing-2-scaled.png",
    accent: "orange",
  },
  {
    id: 3,
    cat: { es: "Estrategia", en: "Strategy" },
    catKey: "Strategy",
    title: { es: "Sistema comercial 360° · PyME industrial", en: "360° Commercial system · Industrial SMB" },
    metric: { es: "Pipeline x2.4 en 90 días", en: "Pipeline x2.4 in 90 days" },
    img: "https://reinventemercadeo.com/wp-content/uploads/2026/03/estrategia-digital-scaled.png",
    accent: "blue",
  },
  {
    id: 4,
    cat: { es: "IA", en: "AI" },
    catKey: "AI",
    title: { es: "WhatsApp AI + CRM conectado", en: "WhatsApp AI + connected CRM" },
    metric: { es: "Respuesta media: 28s", en: "Avg. reply time: 28s" },
    img: "https://reinventemercadeo.com/wp-content/uploads/2026/03/photo-28.jpg",
    accent: "orange",
  },
  {
    id: 5,
    cat: { es: "Branding", en: "Branding" },
    catKey: "Branding",
    title: { es: "Reposicionamiento premium · Cirugía estética", en: "Premium reposition · Aesthetic surgery" },
    metric: { es: "Ticket promedio x2", en: "Avg. ticket x2" },
    img: "https://reinventemercadeo.com/wp-content/uploads/2026/03/branding-4-scaled.png",
    accent: "blue",
  },
  {
    id: 6,
    cat: { es: "Coaching", en: "Coaching" },
    catKey: "Coaching",
    title: { es: "Coaching comercial · Equipo B2B", en: "Sales coaching · B2B team" },
    metric: { es: "+47% cierre efectivo", en: "+47% effective close rate" },
    img: "https://reinventemercadeo.com/wp-content/uploads/2026/03/coaching-comercial-3-scaled.jpg",
    accent: "orange",
  },
];

const FILTER_KEYS = ["All", "AI", "Neuromarketing", "Strategy", "Branding", "Coaching"];

export default function Portfolio() {
  const { t, lang } = useLang();
  const [filter, setFilter] = useState("All");
  const sectionRef = useRef(null);
  const viewportRef = useRef(null);
  const [shift, setShift] = useState(0);

  const filtered = useMemo(
    () => (filter === "All" ? ALL_CASES : ALL_CASES.filter((c) => c.catKey === filter)),
    [filter]
  );

  // Horizontal travel tied to vertical scroll
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start start", "end end"],
  });
  const x = useTransform(scrollYProgress, [0.08, 0.92], [0, -shift]);
  const barScaleX = useTransform(scrollYProgress, [0.08, 0.92], [0, 1]);

  useEffect(() => {
    const measure = () => {
      const el = viewportRef.current;
      if (el) setShift(Math.max(0, el.scrollWidth - el.clientWidth));
    };
    measure();
    const id = setTimeout(measure, 450); // re-measure after filter exit animations
    window.addEventListener("resize", measure);
    return () => {
      clearTimeout(id);
      window.removeEventListener("resize", measure);
    };
  }, [filtered.length]);

  return (
    <section
      ref={sectionRef}
      id="portfolio"
      data-testid="portfolio-section"
      className="relative"
      style={{ height: filtered.length > 2 ? "300vh" : "160vh" }}
    >
      <div className="sticky top-0 h-[100svh] overflow-hidden flex flex-col justify-center">
        <div className="absolute inset-0 bg-radial-orange opacity-50 pointer-events-none" />

        <div className="relative w-full pt-20 sm:pt-16">
          <div className="mx-auto max-w-7xl px-5">
            <motion.div
              initial={{ opacity: 0, y: 22 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.7, ease }}
              className="max-w-3xl"
            >
              <div className="text-[11px] sm:text-xs uppercase tracking-[0.24em] text-brand-orange font-mono-tight mb-3">
                {t.portfolio.eyebrow}
              </div>
              <h2 className="font-display text-3xl sm:text-5xl md:text-[52px] lg:text-6xl font-black tracking-tighter text-white leading-[1.02]">
                {t.portfolio.title}{" "}
                <span className="text-gradient-orange">{t.portfolio.title2}</span>
              </h2>
              <p className="mt-4 text-white/65 leading-relaxed text-[14px] sm:text-base max-w-2xl">
                {t.portfolio.subtitle}
              </p>
            </motion.div>

            {/* Filters */}
            <div className="mt-6 flex flex-wrap gap-2" data-testid="portfolio-filters">
              {FILTER_KEYS.map((k, i) => {
                const label = t.portfolio.filters[i];
                const active = filter === k;
                return (
                  <button
                    key={k}
                    onClick={() => setFilter(k)}
                    data-testid={`filter-${k.toLowerCase()}`}
                    className={`px-4 py-2 rounded-full text-[12.5px] font-medium transition-all border ${
                      active
                        ? "bg-white text-brand-ink border-white"
                        : "bg-white/[0.03] text-white/70 border-white/10 hover:border-white/30 hover:text-white"
                    }`}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Horizontal track driven by vertical scroll */}
          <div ref={viewportRef} className="mt-8 overflow-hidden" data-testid="portfolio-track">
            <motion.div
              style={{ x }}
              className="flex gap-5 w-max pl-[max(1.25rem,calc((100vw-80rem)/2+1.25rem))] pr-10"
            >
              <AnimatePresence>
                {filtered.map((c, i) => (
                  <CaseCard key={c.id} item={c} index={i} lang={lang} />
                ))}
              </AnimatePresence>
            </motion.div>
          </div>

          {/* Scroll progress */}
          <div className="mx-auto max-w-7xl px-5 mt-8">
            <div className="h-[3px] w-full max-w-xs bg-white/8 rounded-full overflow-hidden">
              <motion.div
                style={{ scaleX: barScaleX }}
                className="h-full w-full origin-left rounded-full bg-gradient-to-r from-brand-blue to-brand-orange"
                data-testid="portfolio-progress"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function CaseCard({ item, index, lang }) {
  return (
    <motion.a
      layout
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 10 }}
      transition={{ duration: 0.5, ease, delay: index * 0.04 }}
      whileHover={{ y: -6 }}
      href="https://reinventemercadeo.com/servicios/"
      target="_blank"
      rel="noreferrer"
      data-testid={`portfolio-card-${item.id}`}
      className="group relative w-[76vw] sm:w-[390px] shrink-0 rounded-3xl overflow-hidden glass hover:border-white/20 transition"
    >
      <div className="aspect-[16/10] overflow-hidden bg-brand-ink-2 relative">
        <img
          src={item.img}
          alt={item.title[lang]}
          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0b1220] via-[#0b1220]/30 to-transparent" />
        <span className="absolute top-3 left-3 text-[10.5px] uppercase tracking-[0.2em] font-mono-tight px-2.5 py-1 rounded-full glass text-white/85">
          {item.cat[lang]}
        </span>
      </div>
      <div className="p-5">
        <h3 className="font-display text-base lg:text-lg font-bold text-white leading-snug tracking-tight">
          {item.title[lang]}
        </h3>
        <div className="mt-3 flex items-center justify-between">
          <span
            className={`text-[12.5px] font-medium ${
              item.accent === "blue" ? "text-brand-blue" : "text-brand-orange"
            }`}
          >
            {item.metric[lang]}
          </span>
          <ArrowUpRight className="h-4 w-4 text-white/45 group-hover:text-white transition" />
        </div>
      </div>
    </motion.a>
  );
}
